import backgroundDesktop from '@assets/images/trim.svg';
import backgroundMobile from '@assets/images/trim-mobile.svg';
import { Button, Spinner, Stack } from 'component-library';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { useIsConservation } from '@/hooks/useIsConservation';
import { usePathSet } from '@/hooks/usePathSet';
import { useScreenSize } from '@/hooks/useScreenSize';
import { SignInWithTokenState, useSignInWithToken } from '@/hooks/useSignInWithToken';
import { deleteAllCookies } from '@/lib/storage';
import { NotFoundPage } from '@/pages/not-found';
import { paths } from '@/routing';

export const ImpersonatePage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const { signInState } = useSignInWithToken(searchParams.get('token'));

  if (signInState === SignInWithTokenState.SUCCESS) {
    return <RedirectUser />;
  }

  if ([SignInWithTokenState.IN_PROGRESS].includes(signInState)) {
    return <LoadingState />;
  }

  return <NotFoundPage disableLogging />;
};

const LoadingState: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSmallScreen = useScreenSize() === 'small';
  const [showActions, setShowActions] = useState(false);

  useEffect(() => {
    const time = process.env.NODE_ENV !== 'test' ? 30 * 1000 : 0; // Speed up tests
    const timer = setTimeout(() => setShowActions(true), time);
    return () => clearTimeout(timer);
  }, []);

  const handleClearCookies = () => {
    deleteAllCookies();
    window.location.reload();
  };

  return (
    <div
      className='flex h-screen w-full flex-col items-center justify-between bg-cover'
      style={{ backgroundImage: `url("${isSmallScreen ? backgroundMobile : backgroundDesktop}")` }}
    >
      <Stack
        className='m-auto max-w-2xl items-center rounded-3xl bg-white-100 px-12 py-10 border border-neutral-black-12 min-w-[500px] min-h-[300px]'
        center
        centerMain
      >
        <Stack spacing={6} center>
          <Spinner size={8} className='text-primary-100' />
          <p className='typography-body1 text-center text-text-primary'>{t('login.impersonate.loading')}</p>
          {showActions && (
            <Stack spacing={4}>
              <Button variant='outline' size='small' onClick={handleClearCookies}>
                {t('login.impersonate.clearCookies')}
              </Button>
              <Button size='small' onClick={() => navigate(paths.login)}>
                {t('login.impersonate.backToLogin')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    </div>
  );
};

const RedirectUser: React.FC = () => {
  const pathSet = usePathSet();
  const isConservation = useIsConservation();

  return <Navigate to={isConservation ? pathSet.projects : pathSet.home} />;
};
